import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  SafeAreaView,
  ScrollView,
  Switch,
  Modal,
  Pressable,
  ActivityIndicator,
  Alert,
  Platform,
  Dimensions,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useAuth } from '../services/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { clearAllChats } from '../services/chatService';
import { createTournament as createTournamentSupabase, addTournamentParticipant } from '../services/database';
import { useTheme } from '../contexts/ThemeContext';
import { useIconTheme } from '../contexts/IconThemeContext';
import { useMapSettings, BUFFER_PRESETS } from '../contexts/MapSettingsContext';
import { useMapProvider } from '../contexts/MapProviderContext';
import { getColors, typography, spacing, borderRadius } from '../styles/theme';

const SettingsScreen = ({ navigation }) => {
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { currentIconSet, setIconTheme, iconSetMeta, availableIconSets } = useIconTheme();
  const { viewportBuffer, setViewportBuffer, setMobileDefault, clearUserOverride, globalDefault, globalDefaultMobile, hasUserOverride } = useMapSettings();
  const { showStreetLabels, setShowStreetLabels } = useMapProvider();
  const { user, appUser, clerkUserId, updateUserProfile } = useAuth();
  const { showToast } = useToast();
  const colors = getColors(isDarkMode);
  const isAdmin = appUser?.isAdmin || false;
  const [showIconThemePicker, setShowIconThemePicker] = useState(false);
  const [creatingTestTournament, setCreatingTestTournament] = useState(false);
  const [clearingChats, setClearingChats] = useState(false);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const [uiFadeEnabled, setUiFadeEnabled] = useState(true); // UI fade on map drag
  const [showDistanceInDiscover, setShowDistanceInDiscover] = useState(false);

  const currentThemeMeta = iconSetMeta[currentIconSet];

  // Ensure icon theme picker is closed when Settings screen mounts (never open by default)
  useEffect(() => {
    setShowIconThemePicker(false);
  }, []);

  // Fetch current user profile and UI preferences on mount
  useEffect(() => {
    const fetchProfile = async () => {
      // Use appUser from Clerk/Supabase auth context
      if (appUser) {
        setCurrentUserProfile(appUser);
        setShowDistanceInDiscover(appUser.showDistanceInDiscover ?? false);
      }
    };
    
    const loadPreferences = async () => {
      try {
        let uiFadeValue = null;
        if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
          uiFadeValue = localStorage.getItem('uiFadeEnabled');
        } else {
          uiFadeValue = await AsyncStorage.getItem('uiFadeEnabled');
        }
        if (uiFadeValue !== null) {
          setUiFadeEnabled(JSON.parse(uiFadeValue));
        }
      } catch (error) {
        console.error('Error loading UI preferences:', error);
      }
    };
    
    fetchProfile();
    loadPreferences();
  }, [user]);

  const handleSelectIconTheme = async (themeKey) => {
    await setIconTheme(themeKey);
    setShowIconThemePicker(false);
  };

  const handleToggleUiFade = async (value) => {
    try {
      setUiFadeEnabled(value);
      if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
        localStorage.setItem('uiFadeEnabled', JSON.stringify(value));
      } else {
        await AsyncStorage.setItem('uiFadeEnabled', JSON.stringify(value));
      }
      showToast(
        value 
          ? 'UI fade animation enabled' 
          : 'UI fade animation disabled', 
        'success'
      );
    } catch (error) {
      console.error('Error saving UI fade preference:', error);
    }
  };

  const handleToggleShowDistanceInDiscover = async (value) => {
    if (!appUser?.id) return;
    try {
      setShowDistanceInDiscover(value);
      await updateUserProfile({ showDistanceInDiscover: value });
      showToast(
        value
          ? 'Your distance will be shown in Discover'
          : 'Your distance is now private in Discover',
        'success'
      );
    } catch (error) {
      console.error('Error saving show distance preference:', error);
      setShowDistanceInDiscover(!value); // revert
      showToast('Failed to update preference', 'error');
    }
  };

  // Create a test tournament with mock participants
  const handleCreateTestTournament = async () => {
    if (!currentUserProfile) {
      showToast('Please wait, loading profile...', 'error');
      return;
    }

    setCreatingTestTournament(true);
    try {
      // Create the tournament
      const tournamentInput = {
        clubId: currentUserProfile.id, // Use current user as "club" for testing
        name: `Test Tournament - ${new Date().toLocaleDateString()}`,
        description: 'This is a test tournament created from the admin panel for testing purposes. Test the participant flow by logging in with another account.',
        type: 'SWISS',
        status: 'REGISTRATION', // Start in registration so we can test joining
        totalRounds: 4,
        currentRound: 0,
        allowPhones: true,
        isPaid: false,
        formatConfig: JSON.stringify({
          pairing: 'SWISS',
          allowPhones: true,
          timeControl: '10+5',
          announcements: [
            {
              id: 'ann_welcome',
              message: 'Welcome to the test tournament! This is a demo event.',
              createdAt: new Date().toISOString(),
            },
          ],
        }),
      };

      const tournamentData = {
        club_id: currentUserProfile.id,
        name: tournamentInput.name,
        description: tournamentInput.description,
        type: tournamentInput.type,
        status: tournamentInput.status,
        total_rounds: tournamentInput.totalRounds,
        current_round: tournamentInput.currentRound,
        allow_phones: tournamentInput.allowPhones,
        is_paid: tournamentInput.isPaid,
        format_config: tournamentInput.formatConfig,
      };

      const tournamentResult = await createTournamentSupabase(tournamentData);
      const tournamentId = tournamentResult.id;

      // Add some mock participants
      const mockParticipants = [
        { name: 'Alice Johnson', rating: 1450, seed: 1 },
        { name: 'Bob Smith', rating: 1380, seed: 2 },
        { name: 'Charlie Brown', rating: 1520, seed: 3 },
        { name: 'Diana Ross', rating: 1290, seed: 4 },
        { name: 'Edward King', rating: 1600, seed: 5 },
        { name: 'Fiona Apple', rating: 1340, seed: 6 },
      ];

      await Promise.all(
        mockParticipants.map((participant) =>
          addTournamentParticipant({
            tournament_id: tournamentId,
            guest_name: participant.name,
            rating_snapshot: participant.rating,
            seed_number: participant.seed,
            score: 0,
            has_paid: false,
            payment_status: null,
          })
        )
      );

      showToast('Test tournament created with 6 participants!', 'success');
      
      // Navigate to tournament control (admin view)
      navigation.navigate('TournamentControl', { tournamentId });
    } catch (error) {
      console.error('Error creating test tournament:', error);
      showToast('Failed to create test tournament', 'error');
    } finally {
      setCreatingTestTournament(false);
    }
  };

  // Quick join as a player (for testing participant view)
  const handleJoinTestTournamentAsPlayer = () => {
    // Show alert with input for tournament ID
    Alert.alert(
      'Join Tournament as Player',
      'Enter the tournament ID to join as a test player (you can find this in the tournament URL)',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'View Tournaments',
          onPress: () => navigation.navigate('Tournaments'),
        },
      ]
    );
  };

  // Clear all chats from the database (admin tool)
  const runClearAllChats = async () => {
    setClearingChats(true);
    try {
      const result = await clearAllChats();
      showToast(
        `Cleared ${result.deletedConversations} conversations and ${result.deletedMessages} messages`,
        'success'
      );
    } catch (error) {
      console.error('Error clearing chats:', error);
      showToast('Failed to clear chats: ' + (error.message || 'Unknown error'), 'error');
    } finally {
      setClearingChats(false);
    }
  };

  const handleClearAllChats = () => {
    const message = 'This will permanently delete ALL conversations and messages for ALL users. This cannot be undone.\n\nAre you sure?';
    // On web, Alert.alert often doesn't show; use window.confirm so the dialog appears
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const confirmed = window.confirm(message);
      if (confirmed) runClearAllChats();
    } else {
      Alert.alert(
        'Clear All Chats',
        message,
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Delete All Chats',
            style: 'destructive',
            onPress: runClearAllChats,
          },
        ]
      );
    }
  };

  return (
    <SafeAreaView style={[
      styles.container,
      { backgroundColor: colors.background },
      Platform.OS === 'web' && { height: Dimensions.get('window').height, maxHeight: Dimensions.get('window').height },
    ]}>
      {/* Header */}
      <View style={[styles.header, { borderBottomColor: colors.border }]}>
        <TouchableOpacity 
          style={styles.backButton} 
          onPress={() => navigation.goBack()}
        >
          <Icon name="arrow-back" size={24} color={colors.text} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: colors.text }]}>Settings</Text>
        <View style={styles.headerSpacer} />
      </View>

      <View style={styles.scrollViewWrapper}>
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.content}
          showsVerticalScrollIndicator={true}
          bounces={true}
        >
        {/* Appearance Section */}
        <View style={styles.sectionHeader}>
          <Text style={[styles.sectionTitle, { color: colors.textSecondary }]}>
            Appearance
          </Text>
        </View>

        <View style={[styles.settingCard, { backgroundColor: colors.surface }]}>
          <TouchableOpacity 
            style={styles.settingRow}
            onPress={toggleDarkMode}
            activeOpacity={0.7}
          >
            <View style={styles.settingLeft}>
              <View style={[styles.iconContainer, { backgroundColor: isDarkMode ? '#3D3D3D' : '#F0F0F0' }]}>
                <Icon 
                  name={isDarkMode ? 'dark-mode' : 'light-mode'} 
                  size={22} 
                  color={isDarkMode ? '#FFD700' : '#FF9800'} 
                />
              </View>
              <View style={styles.settingText}>
                <Text style={[styles.settingLabel, { color: colors.text }]}>
                  Dark Mode
                </Text>
                <Text style={[styles.settingDescription, { color: colors.textSecondary }]}>
                  {isDarkMode ? 'Currently using dark theme' : 'Currently using light theme'}
                </Text>
              </View>
            </View>
            <Switch
              value={isDarkMode}
              onValueChange={toggleDarkMode}
              trackColor={{ false: '#E0E0E0', true: '#7FB069' }}
              thumbColor={isDarkMode ? '#FFFFFF' : '#FFFFFF'}
              ios_backgroundColor="#E0E0E0"
            />
          </TouchableOpacity>

          {/* Divider */}
          <View style={[styles.divider, { backgroundColor: colors.border }]} />

          {/* UI Fade Animation Toggle */}
          <TouchableOpacity 
            style={styles.settingRow}
            onPress={() => handleToggleUiFade(!uiFadeEnabled)}
            activeOpacity={0.7}
          >
            <View style={styles.settingLeft}>
              <View style={[styles.iconContainer, { backgroundColor: uiFadeEnabled ? '#E8F5E9' : '#F5F5F5' }]}>
                <Icon 
                  name="visibility" 
                  size={22} 
                  color={uiFadeEnabled ? '#4CAF50' : '#9E9E9E'} 
                />
              </View>
              <View style={styles.settingText}>
                <Text style={[styles.settingLabel, { color: colors.text }]}>
                  UI Fade on Map Drag
                </Text>
                <Text style={[styles.settingDescription, { color: colors.textSecondary }]}>
                  Fade UI elements when panning the map
                </Text>
              </View>
            </View>
            <Switch
              value={uiFadeEnabled}
              onValueChange={handleToggleUiFade}
              trackColor={{ false: '#E0E0E0', true: '#7FB069' }}
              thumbColor={uiFadeEnabled ? '#FFFFFF' : '#FFFFFF'}
              ios_backgroundColor="#E0E0E0"
            />
          </TouchableOpacity>

          {/* Divider */}
          <View style={[styles.divider, { backgroundColor: colors.border }]} />

          {/* Street Names on Map Toggle */}
          <TouchableOpacity 
            style={styles.settingRow}
            onPress={() => setShowStreetLabels(!showStreetLabels)}
            activeOpacity={0.7}
          >
            <View style={styles.settingLeft}>
              <View style={[styles.iconContainer, { backgroundColor: showStreetLabels ? '#E8F5E9' : '#F5F5F5' }]}>
                <Icon 
                  name="label" 
                  size={22} 
                  color={showStreetLabels ? '#4CAF50' : '#9E9E9E'} 
                />
              </View>
              <View style={styles.settingText}>
                <Text style={[styles.settingLabel, { color: colors.text }]}>
                  Street names on map
                </Text>
                <Text style={[styles.settingDescription, { color: colors.textSecondary }]}>
                  {showStreetLabels
                    ? 'Street names are visible on the map'
                    : 'Street names are hidden (cleaner look)'}
                </Text>
              </View>
            </View>
            <Switch
              value={showStreetLabels}
              onValueChange={setShowStreetLabels}
              trackColor={{ false: '#E0E0E0', true: '#7FB069' }}
              thumbColor={showStreetLabels ? '#FFFFFF' : '#FFFFFF'}
              ios_backgroundColor="#E0E0E0"
            />
          </TouchableOpacity>

        </View>

        {/* Privacy Section */}
        <View style={styles.sectionHeader}>
          <Text style={[styles.sectionTitle, { color: colors.textSecondary }]}>
            Privacy
          </Text>
        </View>

        <View style={[styles.settingCard, { backgroundColor: colors.surface }]}>
          <TouchableOpacity 
            style={styles.settingRow}
            onPress={() => handleToggleShowDistanceInDiscover(!showDistanceInDiscover)}
            activeOpacity={0.7}
          >
            <View style={styles.settingLeft}>
              <View style={[styles.iconContainer, { backgroundColor: showDistanceInDiscover ? '#E8F5E9' : '#F5F5F5' }]}>
                <Icon 
                  name="straighten" 
                  size={22} 
                  color={showDistanceInDiscover ? '#4CAF50' : '#9E9E9E'} 
                />
              </View>
              <View style={styles.settingText}>
                <Text style={[styles.settingLabel, { color: colors.text }]}>
                  Show my distance in Discover
                </Text>
                <Text style={[styles.settingDescription, { color: colors.textSecondary }]}>
                  {showDistanceInDiscover
                    ? 'Other players can see how far you are in the Discover list'
                    : 'Your distance is hidden from others (default)'}
                </Text>
              </View>
            </View>
            <Switch
              value={showDistanceInDiscover}
              onValueChange={handleToggleShowDistanceInDiscover}
              trackColor={{ false: '#E0E0E0', true: '#7FB069' }}
              thumbColor={showDistanceInDiscover ? '#FFFFFF' : '#FFFFFF'}
              ios_backgroundColor="#E0E0E0"
            />
          </TouchableOpacity>
        </View>

        {/* Icon Theme Section */}
        <View style={styles.sectionHeader}>
          <Text style={[styles.sectionTitle, { color: colors.textSecondary }]}>
            Map Icons
          </Text>
        </View>

        <View style={[styles.settingCard, { backgroundColor: colors.surface }]}>
          <TouchableOpacity 
            style={styles.settingRow}
            onPress={() => setShowIconThemePicker(true)}
            activeOpacity={0.7}
          >
            <View style={styles.settingLeft}>
              <View style={[styles.iconContainer, { backgroundColor: isDarkMode ? '#3D3D3D' : '#F0F0F0' }]}>
                <Text style={{ fontSize: 22 }}>
                  {currentThemeMeta?.preview?.[0] || '📍'}
                </Text>
              </View>
              <View style={styles.settingText}>
                <Text style={[styles.settingLabel, { color: colors.text }]}>
                  Icon Theme
                </Text>
                <Text style={[styles.settingDescription, { color: colors.textSecondary }]}>
                  {currentThemeMeta?.name || 'Default'} - {currentThemeMeta?.description || 'Classic emoji icons'}
                </Text>
              </View>
            </View>
            <Icon name="chevron-right" size={24} color={colors.textSecondary} />
          </TouchableOpacity>
          
          {/* Preview row */}
          <View style={[styles.iconPreviewRow, { borderTopColor: colors.border }]}>
            {currentThemeMeta?.preview?.map((icon, index) => (
              <Text key={index} style={styles.previewIcon}>{icon}</Text>
            ))}
          </View>
        </View>

        {/* Map Performance Section */}
        <View style={styles.sectionHeader}>
          <Text style={[styles.sectionTitle, { color: colors.textSecondary }]}>
            🗺️ Map Performance
          </Text>
        </View>

        {/* Admin: Set Global Default */}
        {isAdmin && (
          <View style={[styles.settingCard, { backgroundColor: colors.surface, marginBottom: spacing.md }]}>
            <View style={[styles.settingRow, { flexDirection: 'column', alignItems: 'stretch' }]}>
              <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: spacing.sm }}>
                <View style={[styles.iconContainer, { backgroundColor: '#FFF3E0' }]}>
                  <Icon name="admin-panel-settings" size={22} color="#FF9800" />
                </View>
                <View style={styles.settingText}>
                  <Text style={[styles.settingLabel, { color: colors.text }]}>
                    Global Default (Admin)
                  </Text>
                  <Text style={[styles.settingDescription, { color: colors.textSecondary }]}>
                    Default for all users: {BUFFER_PRESETS[Object.keys(BUFFER_PRESETS).find(k => Math.abs(BUFFER_PRESETS[k].value - globalDefault) < 0.01)]?.label || 'Custom'}
                  </Text>
                </View>
              </View>
              
              {/* Admin Buffer Preset Buttons */}
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: spacing.xs }}>
                {Object.entries(BUFFER_PRESETS).map(([key, preset]) => {
                  const isSelected = Math.abs(preset.value - globalDefault) < 0.01;
                  return (
                    <TouchableOpacity
                      key={key}
                      onPress={async () => {
                        try {
                          await setViewportBuffer(preset.value, true, clerkUserId || appUser?.clerkId);
                          showToast('Global default updated', 'success');
                        } catch (error) {
                          console.error('Failed to update global default:', error);
                          const msg = error?.message || '';
                          showToast(
                            msg.includes('Sign-in') ? 'Please sign in and try again' :
                            msg.includes('Only admins') ? 'Admin access required' :
                            msg.includes('function') || msg.includes('does not exist') ? 'Run: supabase db push (migrations 00019, 00020)' :
                            'Failed to update global default',
                            'error'
                          );
                        }
                      }}
                      style={{
                        paddingHorizontal: 12,
                        paddingVertical: 8,
                        borderRadius: 8,
                        backgroundColor: isSelected ? '#FF9800' : (isDarkMode ? '#3D3D3D' : '#F0F0F0'),
                        borderWidth: 1,
                        borderColor: isSelected ? '#FF9800' : 'transparent',
                      }}
                    >
                      <Text style={{
                        color: isSelected ? '#FFFFFF' : colors.text,
                        fontWeight: isSelected ? '600' : '400',
                        fontSize: 13,
                      }}>
                        {preset.label}
                      </Text>
                      <Text style={{
                        color: isSelected ? 'rgba(255,255,255,0.8)' : colors.textSecondary,
                        fontSize: 10,
                        marginTop: 2,
                      }}>
                        {preset.description}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>
          </View>
        )}

        {/* Admin: Global Default for Mobile (map performance) */}
        {isAdmin && (
          <View style={[styles.settingCard, { backgroundColor: colors.surface, marginBottom: spacing.md }]}>
            <View style={[styles.settingRow, { flexDirection: 'column', alignItems: 'stretch' }]}>
              <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: spacing.sm }}>
                <View style={[styles.iconContainer, { backgroundColor: '#E3F2FD' }]}>
                  <Icon name="phone-android" size={22} color="#2196F3" />
                </View>
                <View style={styles.settingText}>
                  <Text style={[styles.settingLabel, { color: colors.text }]}>
                    Global Default for Mobile (Map Performance)
                  </Text>
                  <Text style={[styles.settingDescription, { color: colors.textSecondary }]}>
                    Default for users on mobile: fewer markers = better performance. Current: {BUFFER_PRESETS[Object.keys(BUFFER_PRESETS).find(k => Math.abs(BUFFER_PRESETS[k].value - globalDefaultMobile) < 0.01)]?.label || 'Custom'}
                  </Text>
                </View>
              </View>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: spacing.xs }}>
                {Object.entries(BUFFER_PRESETS).map(([key, preset]) => {
                  const isSelected = Math.abs(preset.value - globalDefaultMobile) < 0.01;
                  return (
                    <TouchableOpacity
                      key={key}
                      onPress={async () => {
                        try {
                          await setMobileDefault(preset.value, clerkUserId || appUser?.clerkId);
                          showToast('Mobile default updated', 'success');
                        } catch (error) {
                          console.error('Failed to update mobile default:', error);
                          const msg = error?.message || '';
                          showToast(
                            msg.includes('Sign-in') ? 'Please sign in and try again' :
                            msg.includes('Only admins') ? 'Admin access required' :
                            'Failed to update mobile default',
                            'error'
                          );
                        }
                      }}
                      style={{
                        paddingHorizontal: 12,
                        paddingVertical: 8,
                        borderRadius: 8,
                        backgroundColor: isSelected ? '#2196F3' : (isDarkMode ? '#3D3D3D' : '#F0F0F0'),
                        borderWidth: 1,
                        borderColor: isSelected ? '#2196F3' : 'transparent',
                      }}
                    >
                      <Text style={{
                        color: isSelected ? '#FFFFFF' : colors.text,
                        fontWeight: isSelected ? '600' : '400',
                        fontSize: 13,
                      }}>
                        {preset.label}
                      </Text>
                      <Text style={{
                        color: isSelected ? 'rgba(255,255,255,0.8)' : colors.textSecondary,
                        fontSize: 10,
                        marginTop: 2,
                      }}>
                        {preset.description}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>
          </View>
        )}

        {/* User: Personal Override */}
        <View style={[styles.settingCard, { backgroundColor: colors.surface }]}>
          <View style={[styles.settingRow, { flexDirection: 'column', alignItems: 'stretch' }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: spacing.sm }}>
              <View style={[styles.iconContainer, { backgroundColor: '#E8F5E9' }]}>
                <Icon name="visibility" size={22} color="#4CAF50" />
              </View>
              <View style={styles.settingText}>
                <Text style={[styles.settingLabel, { color: colors.text }]}>
                  Your Setting {hasUserOverride && '(Override)'}
                </Text>
                <Text style={[styles.settingDescription, { color: colors.textSecondary }]}>
                  {hasUserOverride 
                    ? `Using your preference (default: ${BUFFER_PRESETS[Object.keys(BUFFER_PRESETS).find(k => Math.abs(BUFFER_PRESETS[k].value - (Platform.OS !== 'web' ? globalDefaultMobile : globalDefault)) < 0.01)]?.label || 'Custom'})`
                    : `Using global default: ${BUFFER_PRESETS[Object.keys(BUFFER_PRESETS).find(k => Math.abs(BUFFER_PRESETS[k].value - (Platform.OS !== 'web' ? globalDefaultMobile : globalDefault)) < 0.01)]?.label || 'Custom'}`}
                </Text>
              </View>
            </View>
            
            {/* User Buffer Preset Buttons */}
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: spacing.xs }}>
              {Object.entries(BUFFER_PRESETS).map(([key, preset]) => {
                const isSelected = Math.abs(preset.value - viewportBuffer) < 0.01;
                return (
                  <TouchableOpacity
                    key={key}
                    onPress={async () => {
                      try {
                        await setViewportBuffer(preset.value, false);
                        showToast('Your preference saved', 'success');
                      } catch (error) {
                        showToast('Failed to save preference', 'error');
                      }
                    }}
                    style={{
                      paddingHorizontal: 12,
                      paddingVertical: 8,
                      borderRadius: 8,
                      backgroundColor: isSelected ? '#4CAF50' : (isDarkMode ? '#3D3D3D' : '#F0F0F0'),
                      borderWidth: 1,
                      borderColor: isSelected ? '#4CAF50' : 'transparent',
                    }}
                  >
                    <Text style={{
                      color: isSelected ? '#FFFFFF' : colors.text,
                      fontWeight: isSelected ? '600' : '400',
                      fontSize: 13,
                    }}>
                      {preset.label}
                    </Text>
                    <Text style={{
                      color: isSelected ? 'rgba(255,255,255,0.8)' : colors.textSecondary,
                      fontSize: 10,
                      marginTop: 2,
                    }}>
                      {preset.description}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>

            {/* Clear Override Button */}
            {hasUserOverride && (
              <TouchableOpacity
                onPress={async () => {
                  try {
                    await clearUserOverride();
                    showToast('Using global default', 'success');
                  } catch (error) {
                    showToast('Failed to clear override', 'error');
                  }
                }}
                style={{
                  marginTop: spacing.sm,
                  paddingVertical: 8,
                  paddingHorizontal: 12,
                  borderRadius: 8,
                  backgroundColor: isDarkMode ? '#3D3D3D' : '#F0F0F0',
                  alignSelf: 'flex-start',
                }}
              >
                <Text style={{ color: colors.textSecondary, fontSize: 12 }}>
                  Reset to Global Default
                </Text>
              </TouchableOpacity>
            )}
          </View>
        </View>

        {/* Admin / Developer Tools Section */}
        <View style={styles.sectionHeader}>
          <Text style={[styles.sectionTitle, { color: colors.textSecondary }]}>
            🛠️ Admin Test Tools
          </Text>
        </View>

        <View style={[styles.settingCard, { backgroundColor: colors.surface }]}>
          {/* Create Test Tournament */}
          <TouchableOpacity 
            style={styles.settingRow}
            onPress={handleCreateTestTournament}
            activeOpacity={0.7}
            disabled={creatingTestTournament}
          >
            <View style={styles.settingLeft}>
              <View style={[styles.iconContainer, { backgroundColor: '#FFF3E0' }]}>
                <Icon name="emoji-events" size={22} color="#FF9800" />
              </View>
              <View style={styles.settingText}>
                <Text style={[styles.settingLabel, { color: colors.text }]}>
                  Create Test Tournament
                </Text>
                <Text style={[styles.settingDescription, { color: colors.textSecondary }]}>
                  Creates a Swiss tournament with 6 mock participants
                </Text>
              </View>
            </View>
            {creatingTestTournament ? (
              <ActivityIndicator size="small" color="#FF9800" />
            ) : (
              <Icon name="add-circle" size={24} color="#FF9800" />
            )}
          </TouchableOpacity>

          {/* Divider */}
          <View style={[styles.divider, { backgroundColor: colors.border }]} />

          {/* View All Tournaments */}
          <TouchableOpacity 
            style={styles.settingRow}
            onPress={() => navigation.navigate('Tournaments')}
            activeOpacity={0.7}
          >
            <View style={styles.settingLeft}>
              <View style={[styles.iconContainer, { backgroundColor: '#E3F2FD' }]}>
                <Icon name="list-alt" size={22} color="#2196F3" />
              </View>
              <View style={styles.settingText}>
                <Text style={[styles.settingLabel, { color: colors.text }]}>
                  View All Tournaments
                </Text>
                <Text style={[styles.settingDescription, { color: colors.textSecondary }]}>
                  Browse and manage existing tournaments
                </Text>
              </View>
            </View>
            <Icon name="chevron-right" size={24} color={colors.textSecondary} />
          </TouchableOpacity>

          {/* Divider */}
          <View style={[styles.divider, { backgroundColor: colors.border }]} />

          {/* Test Player View Instructions */}
          <View style={[styles.settingRow, { paddingVertical: spacing.md }]}>
            <View style={styles.settingLeft}>
              <View style={[styles.iconContainer, { backgroundColor: '#E8F5E9' }]}>
                <Icon name="person" size={22} color="#4CAF50" />
              </View>
              <View style={styles.settingText}>
                <Text style={[styles.settingLabel, { color: colors.text }]}>
                  Test as Player
                </Text>
                <Text style={[styles.settingDescription, { color: colors.textSecondary }]}>
                  Log in with a player account to test the participant view. Go to Tournaments → tap a tournament → "Player View"
                </Text>
              </View>
            </View>
          </View>

          {/* Divider */}
          <View style={[styles.divider, { backgroundColor: colors.border }]} />

          {/* Clear All Chats */}
          <TouchableOpacity 
            style={styles.settingRow}
            onPress={handleClearAllChats}
            activeOpacity={0.7}
            disabled={clearingChats}
          >
            <View style={styles.settingLeft}>
              <View style={[styles.iconContainer, { backgroundColor: '#FFEBEE' }]}>
                <Icon name="delete-sweep" size={22} color="#F44336" />
              </View>
              <View style={styles.settingText}>
                <Text style={[styles.settingLabel, { color: colors.text }]}>
                  Clear All Chats
                </Text>
                <Text style={[styles.settingDescription, { color: colors.textSecondary }]}>
                  Delete all conversations and messages for all users
                </Text>
              </View>
            </View>
            {clearingChats ? (
              <ActivityIndicator size="small" color="#F44336" />
            ) : (
              <Icon name="warning" size={24} color="#F44336" />
            )}
          </TouchableOpacity>
        </View>

        {/* Quick Links */}
        <View style={styles.sectionHeader}>
          <Text style={[styles.sectionTitle, { color: colors.textSecondary }]}>
            Quick Access
          </Text>
        </View>

        <View style={[styles.settingCard, { backgroundColor: colors.surface }]}>
          <TouchableOpacity 
            style={styles.settingRow}
            onPress={() => navigation.navigate('Profile')}
            activeOpacity={0.7}
          >
            <View style={styles.settingLeft}>
              <View style={[styles.iconContainer, { backgroundColor: '#F3E5F5' }]}>
                <Icon name="account-circle" size={22} color="#9C27B0" />
              </View>
              <View style={styles.settingText}>
                <Text style={[styles.settingLabel, { color: colors.text }]}>
                  My Profile
                </Text>
                <Text style={[styles.settingDescription, { color: colors.textSecondary }]}>
                  View and edit your profile
                </Text>
              </View>
            </View>
            <Icon name="chevron-right" size={24} color={colors.textSecondary} />
          </TouchableOpacity>

          <View style={[styles.divider, { backgroundColor: colors.border }]} />

          <TouchableOpacity 
            style={styles.settingRow}
            onPress={() => navigation.navigate('Map')}
            activeOpacity={0.7}
          >
            <View style={styles.settingLeft}>
              <View style={[styles.iconContainer, { backgroundColor: '#E0F7FA' }]}>
                <Icon name="map" size={22} color="#00BCD4" />
              </View>
              <View style={styles.settingText}>
                <Text style={[styles.settingLabel, { color: colors.text }]}>
                  Map View
                </Text>
                <Text style={[styles.settingDescription, { color: colors.textSecondary }]}>
                  View players and tournaments on the map
                </Text>
              </View>
            </View>
            <Icon name="chevron-right" size={24} color={colors.textSecondary} />
          </TouchableOpacity>
        </View>

        {/* Icon Theme Picker Modal - only mount when open so it never shows on initial load */}
        {showIconThemePicker && (
          <Modal
            visible
            transparent
            animationType="slide"
            onRequestClose={() => setShowIconThemePicker(false)}
          >
            <Pressable 
              style={styles.modalOverlay}
              onPress={() => setShowIconThemePicker(false)}
            >
              <Pressable style={[styles.modalContent, { backgroundColor: colors.surface }]}>
                <View style={[styles.modalHeader, { borderBottomColor: colors.border }]}>
                  <Text style={[styles.modalTitle, { color: colors.text }]}>Choose Icon Theme</Text>
                  <TouchableOpacity onPress={() => setShowIconThemePicker(false)}>
                    <Icon name="close" size={24} color={colors.text} />
                  </TouchableOpacity>
                </View>
                
                <ScrollView style={styles.themeList}>
                  {availableIconSets.map((themeKey) => {
                    const meta = iconSetMeta[themeKey];
                    const isSelected = currentIconSet === themeKey;
                    
                    return (
                      <TouchableOpacity
                        key={themeKey}
                        style={[
                          styles.themeOption,
                          { 
                            backgroundColor: isSelected 
                              ? (isDarkMode ? 'rgba(127, 176, 105, 0.2)' : 'rgba(127, 176, 105, 0.1)')
                              : 'transparent',
                            borderColor: isSelected ? colors.primary : colors.border,
                          }
                        ]}
                        onPress={() => handleSelectIconTheme(themeKey)}
                      >
                        <View style={styles.themeOptionContent}>
                          <View style={styles.themeInfo}>
                            <Text style={[styles.themeName, { color: colors.text }]}>
                              {meta?.name || themeKey}
                            </Text>
                            <Text style={[styles.themeDescription, { color: colors.textSecondary }]}>
                              {meta?.description || ''}
                            </Text>
                          </View>
                          <View style={styles.themePreview}>
                            {meta?.preview?.map((icon, index) => (
                              <Text key={index} style={styles.themePreviewIcon}>{icon}</Text>
                            ))}
                          </View>
                        </View>
                        {isSelected && (
                          <Icon name="check-circle" size={24} color={colors.primary} style={styles.checkIcon} />
                        )}
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>
              </Pressable>
            </Pressable>
          </Modal>
        )}
      </ScrollView>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: 0,
  },
  scrollViewWrapper: {
    flex: 1,
    minHeight: 0,
  },
  scrollView: {
    flex: 1,
    minHeight: 0,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
  },
  backButton: {
    padding: spacing.sm,
    marginLeft: -spacing.sm,
  },
  headerTitle: {
    fontSize: typography.h4,
    fontWeight: typography.bold,
  },
  headerSpacer: {
    width: 40,
  },
  content: {
    padding: spacing.md,
    paddingBottom: spacing.xxl,
  },
  sectionHeader: {
    marginBottom: spacing.sm,
    marginTop: spacing.sm,
  },
  sectionTitle: {
    fontSize: typography.caption,
    fontWeight: typography.semiBold,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  settingCard: {
    borderRadius: borderRadius.lg,
    overflow: 'hidden',
  },
  settingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
  },
  settingLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: borderRadius.md,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.md,
  },
  settingText: {
    flex: 1,
  },
  settingLabel: {
    fontSize: typography.body,
    fontWeight: typography.medium,
  },
  settingDescription: {
    fontSize: typography.caption,
    marginTop: 2,
  },
  iconPreviewRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 16,
    paddingVertical: spacing.md,
    borderTopWidth: 1,
  },
  previewIcon: {
    fontSize: 28,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '70%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: spacing.md,
    borderBottomWidth: 1,
  },
  modalTitle: {
    fontSize: typography.h4,
    fontWeight: typography.semiBold,
  },
  themeList: {
    padding: spacing.md,
  },
  themeOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    marginBottom: spacing.sm,
    borderRadius: borderRadius.lg,
    borderWidth: 2,
  },
  themeOptionContent: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  themeInfo: {
    flex: 1,
  },
  themeName: {
    fontSize: typography.body,
    fontWeight: typography.semiBold,
    marginBottom: 2,
  },
  themeDescription: {
    fontSize: typography.caption,
  },
  themePreview: {
    flexDirection: 'row',
    gap: 8,
    marginLeft: spacing.md,
  },
  themePreviewIcon: {
    fontSize: 24,
  },
  checkIcon: {
    marginLeft: spacing.sm,
  },
  divider: {
    height: 1,
    marginHorizontal: spacing.md,
  },
});

export default SettingsScreen;

